import { useCallback, useEffect, useState } from 'react';
import { IoIosClose } from 'react-icons/io';
import { GiHamburgerMenu } from 'react-icons/gi';
import { getRegulationSimplify } from 'services/engagementActions.service';
import Collapsible from 'react-collapsible';

import './styles.scss';

interface Titles {
  title: string;
  id: string;
  subtitles?: {
    title: string;
    id: string;
  }[];
}

interface ProcessHtmlResult {
  html: string;
  titles: Titles[];
}

export default function SimplifiqueModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [topicsRegulation, setTopicsRegulation] = useState<any>();
  const [isClicked, setIsClicked] = useState(false);
  const [hash, setHash] = useState<string>('');
  const [openCollapsibles, setOpenCollapsibles] = useState<Record<string, boolean>>({});

  const handleTriggerClick = (id: string) => {
    setOpenCollapsibles(prevState => ({
      [id]: !prevState[id],
    }));
  };

  const processHtml = useCallback((html: string): ProcessHtmlResult => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const titleElements = tempDiv.querySelectorAll('h1, h2, h3');
    const titles: { title: string; id: string; subtitles: any[] }[] = [];

    titleElements.forEach((titleElement, index) => {
      const titleText = titleElement.textContent;

      if (!!titleText) {
        const titleId = titleText
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^\w\s]/gi, '')
          .replace(/\d+/g, '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .split(' ')
          .map((word, index) => (index === 0 ? word : word[0].toUpperCase() + word.slice(1)))
          .join('');

        titleElement.setAttribute('id', titleId);

        const subtitles: any = [];
        let nextElement = titleElement.nextElementSibling;
        const nextTitleElement = titleElements[index + 1] || tempDiv.lastElementChild;

        while (nextElement && nextElement !== nextTitleElement) {
          if (/^H[4-6]$/.test(nextElement.tagName)) {
            const subtitleText = nextElement.textContent;

            if (!!subtitleText) {
              const subtitleId = subtitleText
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^\w\s]/gi, '')
                .replace(/\d+/g, '')
                .trim()
                .toLowerCase()
                .replace(/\s+/g, ' ')
                .split(' ')
                .map((word, index) => (index === 0 ? word : word[0].toUpperCase() + word.slice(1)))
                .join('');
              nextElement.setAttribute('id', subtitleId);

              subtitles.push({ title: subtitleText, id: subtitleId });
            }
          }

          nextElement = nextElement.nextElementSibling;
        }

        titles.push({ title: titleText, id: titleId, subtitles });
      }
    });

    return { html: tempDiv.innerHTML, titles };
  }, []);

  const loadContent = useCallback(async () => {
    if (loading) return;

    setLoading(true);
    try {
      const data = await getRegulationSimplify();
      if (data) {
        const item = processHtml(data.regulation_text);
        setContent({ ...data, regulation_text: item.html });
        setTopicsRegulation(item.titles);
        setIsOpen(true);
      }
    } catch (error) {
      console.error('Erro ao carregar conteúdo do Simplifique:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, processHtml]);

  const handleOpen = useCallback(() => {
    if (content) {
      setIsOpen(true);
    } else {
      loadContent();
    }
  }, [content, loadContent]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const changeUrl = useCallback((hashItem: string) => {
    window.location.hash = hashItem;
    setHash(hashItem);
  }, []);

  const handleTopicsMenu = useCallback((item: Titles) => {
    if (!!item?.subtitles?.length && item.subtitles?.length > 0) {
      return (
        <Collapsible
          handleTriggerClick={() => handleTriggerClick(item.id)}
          open={openCollapsibles[item.id]}
          onOpen={() => changeUrl(`#${item.id}`)}
          trigger={item.title}
          key={item.id}
        >
          <ul>
            {item.subtitles.map((subItem: any, index: number) => (
              <li
                onClick={() => {
                  setIsClicked(false);
                }}
                className="mt-2 ml-4"
                key={subItem.id + '_' + index}
              >
                <a
                  className={`${
                    '#' + subItem.id === hash ? 'font-bold' : 'font-thin'
                  } hover:no-underline hover:text-white hover:font-bold active:text-white active:no-underline truncate w-full block`}
                  href={`#${subItem.id}`}
                  title={subItem.title}
                >
                  {subItem.title}
                </a>
              </li>
            ))}
          </ul>
        </Collapsible>
      );
    } else if (item.id && item.title && item.subtitles) {
      return (
        <p className="w-full m-0" key={item.id}>
          <a
            className={`${'#' + item.id === hash && 'active-item'} menu-item truncate w-full block hover:no-underline`}
            href={`#${item.id}`}
            title={item.title}
            onClick={() => {
              setOpenCollapsibles({});
              setIsClicked(false);
            }}
          >
            {item.title}
          </a>
        </p>
      );
    }
  }, [hash, openCollapsibles, changeUrl]);

  useEffect(() => {
    window.addEventListener('open-simplifique-modal', handleOpen);
    return () => {
      window.removeEventListener('open-simplifique-modal', handleOpen);
    };
  }, [handleOpen]);

  useEffect(() => {
    if (isOpen) {
      setHash(window.location.hash);
    }
  }, [isOpen]);

  if (!isOpen || !content) return null;

  return (
    <div className="z-50 simplifique-modal-overlay fixed bg-black bg-opacity-80 top-0 left-0 w-screen h-screen flex items-center justify-center">
      <div className="relative border-2 border-solid border-electric-blue bg-oxford-blue rounded-lg max-w-[900px] w-full p-4 lg:p-10 flex flex-col items-center mx-5">
        <button
          className="absolute -right-5 -top-5 bg-purple rounded-full w-10 h-10 p-0 flex justify-center items-center z-50 border-2 border-transparent cursor-pointer hover:border-white transition-colors"
          onClick={handleClose}
          aria-label="Fechar"
        >
          <IoIosClose color="white" size={30} />
        </button>

        {/* <h1 className="text-center text-yellow text-2xl mb-4">Regulamento Simplificado</h1> */}

        <div id="menu-regulamento" className="flex w-full h-[60vh] mb-4">
          {!isClicked && topicsRegulation && topicsRegulation.length > 0 && (
            <button 
              onClick={() => setIsClicked(!isClicked)} 
              className="fixed lg:hidden top-24 left-5 bg-purple p-2 rounded z-50"
            >
              <GiHamburgerMenu size={20} />
            </button>
          )}

          {topicsRegulation && topicsRegulation.length > 0 && (
            <div
              className={`${isClicked ? 'w-full block' : 'hidden lg:block'} lg:w-[25%] pr-4 flex-shrink-0`}
            >
              <div className="content-menu overflow-y-auto h-full">
                <h3 className="text-lg mb-3 text-yellow">Estrutura de tópicos</h3>
                <div className="pb-4">
                  {topicsRegulation.map((item: any) => handleTopicsMenu(item))}
                </div>
              </div>
            </div>
          )}

          <div className={`simplifique-content text-white w-full ${topicsRegulation && topicsRegulation.length > 0 ? 'lg:w-[75%]' : ''} overflow-y-auto lg:p-3`}>
            {content?.regulation_text && (
              <div dangerouslySetInnerHTML={{ __html: content.regulation_text }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
